import React, { useState, useEffect } from 'react';
import { Upload, X, Loader2, Image as ImageIcon } from 'lucide-react';
import Swal from 'sweetalert2';

const compressImage = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const maxWidth = 1200;
        const maxHeight = 1200;
        let width = img.width;
        let height = img.height;

        if (width > maxWidth || height > maxHeight) {
          if (width > height) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }
        }

        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Compress to JPEG format with 0.75 quality for small string size & high visual quality
        const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
        resolve(dataUrl);
      };
      img.onerror = (error) => reject(error);
      img.src = e.target.result;
    };
    reader.onerror = (error) => reject(error);
    reader.readAsDataURL(file);
  });
};

const ImageUpload = ({ initialImage, onUploadSuccess, folder = 'general', disabled = false }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(initialImage || '');

  // Update preview if initialImage changes externally
  useEffect(() => {
    setPreview(initialImage || '');
  }, [initialImage]);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);

    try {
      const compressedBase64 = await compressImage(file);
      setPreview(compressedBase64);
      onUploadSuccess(compressedBase64);

      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 2000,
        timerProgressBar: true
      });
      Toast.fire({
        icon: 'success',
        title: 'Image uploaded & optimized'
      });
    } catch (error) {
      console.error("FileReader / Canvas Error:", error);
      Swal.fire('Error', 'Failed to process image', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = () => {
    setPreview('');
    onUploadSuccess('');
  };

  const inputId = `file-upload-${folder}`;

  return (
    <div className="space-y-4">
      <div
        className={`relative group rounded-3xl overflow-hidden border-2 border-dashed transition-all ${
          preview ? 'border-sugarcane-green bg-gray-50' : (disabled ? 'border-gray-200 bg-gray-50/50' : 'border-gray-200 hover:border-sugarcane-green bg-gray-50/50')
        }`}
        style={{ minHeight: '200px' }}
      >
        {preview ? (
          <div className="relative w-full h-full min-h-[200px]">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-full object-cover rounded-2xl"
            />
            <div className={`absolute inset-0 bg-black/40 transition-opacity flex items-center justify-center space-x-3 ${disabled ? 'hidden' : 'opacity-0 group-hover:opacity-100'}`}>
              <button
                type="button"
                onClick={() => document.getElementById(inputId)?.click()}
                className="p-3 bg-white text-sugarcane-charcoal rounded-xl hover:bg-sugarcane-green hover:text-white transition-all shadow-lg"
              >
                <Upload size={20} />
              </button>
              <button
                type="button"
                onClick={removeImage}
                className="p-3 bg-white text-red-500 rounded-xl hover:bg-red-500 hover:text-white transition-all shadow-lg"
              >
                <X size={20} />
              </button>
            </div>
          </div>
        ) : (
          <div
            onClick={() => !disabled && document.getElementById(inputId)?.click()}
            className={`w-full h-full min-h-[200px] flex flex-col items-center justify-center p-8 space-y-4 ${disabled ? '' : 'cursor-pointer'}`}
          >
            <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center text-gray-300 shadow-sm group-hover:text-sugarcane-green transition-colors">
              <ImageIcon size={32} />
            </div>
            <div className="text-center">
              <p className="text-sm font-black text-sugarcane-charcoal uppercase tracking-widest">Select Image</p>
              <p className="text-xs text-gray-400 font-medium mt-1">Auto-compressed & optimized</p>
            </div>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 z-20">
            <Loader2 className="animate-spin text-sugarcane-green mb-4" size={40} />
            <p className="text-xs font-black text-sugarcane-green mt-3 uppercase tracking-widest italic animate-pulse">Optimizing Image...</p>
          </div>
        )}
      </div>

      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleUpload}
        disabled={uploading || disabled}
      />
    </div>
  );
};

export default ImageUpload;
